import React from 'react'
import { apiGet, apiPost } from '../../../lib/api'
import { SERVICES, CONTACT_VISITS } from '../../../lib/endpoints'
import type { Service, ContactVisit } from '../../../types'
import { useDashboard } from '../DashboardContext'

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text)',
  marginBottom: 6,
  display: 'block',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  cursor: 'pointer',
}

interface LogVisitModalProps {
  open: boolean
  contactId: string
  contactName: string
  onClose: () => void
  onSaved: () => void
}

type ItemForm = {
  // Stable key for React reconciliation when rows are added/removed.
  key: number
  serviceId: string
  quantity: string
  unitPrice: string
}

type VisitForm = {
  visitDate: string
  items: ItemForm[]
  staffName: string
  totalAmount: string
  notes: string
}

function todayString(): string {
  return new Date().toLocaleDateString('sv-SE')
}

let _itemKeySeq = 0
function emptyItem(): ItemForm {
  return { key: ++_itemKeySeq, serviceId: '', quantity: '1', unitPrice: '' }
}

function autoTotal(items: ItemForm[]): number {
  let sum = 0
  for (const it of items) {
    const price = Number(it.unitPrice)
    const qty = Number(it.quantity || '1')
    if (Number.isFinite(price) && price > 0 && Number.isFinite(qty) && qty > 0) {
      sum += price * qty
    }
  }
  return sum
}

export function LogVisitModal({ open, contactId, contactName, onClose, onSaved }: LogVisitModalProps) {
  const { setActivePage } = useDashboard()
  const [services, setServices] = React.useState<Service[]>([])
  const [form, setForm] = React.useState<VisitForm>({
    visitDate: todayString(),
    items: [emptyItem()],
    staffName: '',
    totalAmount: '',
    notes: '',
  })
  const [totalEdited, setTotalEdited] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    // Reset form on open
    setForm({
      visitDate: todayString(),
      items: [emptyItem()],
      staffName: '',
      totalAmount: '',
      notes: '',
    })
    setTotalEdited(false)
    setError(null)
    // Fetch active offerings for the per-item dropdown
    apiGet<Service[]>(SERVICES)
      .then(data => setServices(data.filter(s => s.active)))
      .catch(() => setServices([]))
  }, [open])

  function updateItem(key: number, patch: Partial<ItemForm>) {
    setForm(v => {
      const items = v.items.map(it => (it.key === key ? { ...it, ...patch } : it))
      // If user hasn't overridden the total, keep it in sync with the line items.
      const next: VisitForm = { ...v, items }
      if (!totalEdited) {
        const sum = autoTotal(items)
        next.totalAmount = sum > 0 ? sum.toFixed(2) : ''
      }
      return next
    })
  }

  function addItem() {
    setForm(v => ({ ...v, items: [...v.items, emptyItem()] }))
  }

  function removeItem(key: number) {
    setForm(v => {
      const items = v.items.length === 1 ? [emptyItem()] : v.items.filter(it => it.key !== key)
      const next: VisitForm = { ...v, items }
      if (!totalEdited) {
        const sum = autoTotal(items)
        next.totalAmount = sum > 0 ? sum.toFixed(2) : ''
      }
      return next
    })
  }

  async function handleSubmit() {
    if (!form.visitDate.trim()) { setError('Visit date is required.'); return }
    const validItems = form.items.filter(it => it.serviceId)
    if (validItems.length === 0) { setError('Please add at least one offering.'); return }
    setBusy(true)
    setError(null)
    try {
      const items = validItems.map(it => ({
        serviceId: it.serviceId,
        quantity: it.quantity.trim() ? Number(it.quantity) : 1,
        unitPrice: it.unitPrice.trim() ? Number(it.unitPrice) : null,
      }))
      await apiPost<ContactVisit, Record<string, unknown>>(CONTACT_VISITS(contactId), {
        visitDate: form.visitDate,
        // Primary item for backward-compat: first one in the list.
        serviceId: validItems[0].serviceId,
        staffName: form.staffName.trim() || null,
        totalAmount: form.totalAmount.trim() ? Number(form.totalAmount) : null,
        notes: form.notes.trim() || null,
        items,
      })
      onSaved()
      onClose()
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Failed to log visit')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const noOfferings = services.length === 0

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 95,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
      onClick={() => { if (busy) return; onClose() }}
    >
      <div
        className="panel"
        style={{ width: 'min(620px, 96vw)', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: 22 }}>Log Visit</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{contactName}</div>
          </div>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <form onSubmit={e => { e.preventDefault(); void handleSubmit() }}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Visit Date *</label>
            <input
              type="date"
              style={inputStyle}
              value={form.visitDate}
              onChange={e => setForm(v => ({ ...v, visitDate: e.target.value }))}
            />
          </div>

          {noOfferings && (
            <div style={{
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '12px 14px',
              marginBottom: 16,
              fontSize: 13,
              color: 'var(--text2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}>
              <span>You haven't added any offerings yet — add at least one (a service or a product) before logging a visit.</span>
              <button
                type="button"
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 12px', whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => { onClose(); setActivePage('services') }}
              >
                Add offerings
              </button>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Items *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.items.map((it, idx) => {
                const svc = services.find(s => s.id === it.serviceId) ?? null
                const isProduct = svc?.offeringType === 'PRODUCT'
                return (
                  <div
                    key={it.key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 70px 110px auto',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <select
                        style={{
                          ...selectStyle,
                          cursor: noOfferings ? 'not-allowed' : 'pointer',
                          opacity: noOfferings ? 0.6 : 1,
                        }}
                        value={it.serviceId}
                        onChange={e => {
                          const next = services.find(s => s.id === e.target.value)
                          updateItem(it.key, {
                            serviceId: e.target.value,
                            // Default unit price to the catalogue price when first picking the offering.
                            unitPrice: it.unitPrice || (next?.price != null ? String(next.price) : ''),
                          })
                        }}
                        disabled={noOfferings}
                      >
                        <option value="" disabled>
                          {noOfferings ? 'No offerings — add one first' : (idx === 0 ? 'Select an offering…' : 'Add another offering…')}
                        </option>
                        {services.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.name}{s.offeringType === 'PRODUCT' ? ' (product)' : ''}{s.price != null ? ` — $${s.price.toFixed(2)}` : ''}
                          </option>
                        ))}
                      </select>
                      <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 11, color: 'var(--text2)' }}>▼</span>
                    </div>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      style={inputStyle}
                      value={it.quantity}
                      onChange={e => updateItem(it.key, { quantity: e.target.value })}
                      placeholder="Qty"
                      title={isProduct ? 'How many of this product' : 'Quantity'}
                    />
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      style={inputStyle}
                      value={it.unitPrice}
                      onChange={e => updateItem(it.key, { unitPrice: e.target.value })}
                      placeholder="Unit price"
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '6px 10px', fontSize: 12 }}
                      onClick={() => removeItem(it.key)}
                      aria-label="Remove item"
                      title="Remove item"
                    >
                      ×
                    </button>
                  </div>
                )
              })}
              <button
                type="button"
                className="btn-secondary"
                style={{ fontSize: 12, padding: '6px 12px', alignSelf: 'flex-start' }}
                onClick={addItem}
                disabled={noOfferings}
              >
                + Add another item
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Staff Name</label>
              <input
                type="text"
                maxLength={120}
                style={inputStyle}
                value={form.staffName}
                onChange={e => setForm(v => ({ ...v, staffName: e.target.value }))}
                placeholder="e.g. Sarah"
              />
            </div>
            <div>
              <label style={labelStyle}>Total Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                style={inputStyle}
                value={form.totalAmount}
                onChange={e => {
                  setTotalEdited(true)
                  setForm(v => ({ ...v, totalAmount: e.target.value }))
                }}
                placeholder="auto from items"
              />
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                {totalEdited ? 'Custom total (won\'t auto-update)' : 'Auto-summed from items above'}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              rows={3}
              maxLength={500}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
              value={form.notes}
              onChange={e => setForm(v => ({ ...v, notes: e.target.value }))}
              placeholder="Optional notes about the visit…"
            />
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Log Visit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
